/**
 * WP8d / #46620 — verification shell search palette.
 *
 * Cmd/Ctrl+K, "/" (when not typing), and the top-bar search button open this
 * overlay. Results come from search.shellGet with the active Demo/Real env.
 * Choosing a row navigates and closes. Capture-phase hotkeys swallow the old
 * DockBar Cmd+K so chat stays on the dock and search stays here.
 */

import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { apiCall, useApi } from "@excellent/ui/api";
import { search, type SearchHit } from "@excellent/ui/api/contracts/search";
import { tasks } from "@excellent/ui/api/contracts/tasks";
import { OBJECT_PEEK_OPEN_EVENT } from "@excellent/ui";
import { objectUidFromHref } from "@excellent/ui/path-label";
import { Icon } from "./icons";
import { useVerificationEnvOptional } from "./env-provider";
import {
  buildSearchRows,
  shellSearchQuery,
  type SearchRow,
} from "./search-commands";
import {
  CLOSE_VERIFICATION_SEARCH_EVENT,
  OPEN_VERIFICATION_SEARCH_EVENT,
  decideSearchHotkey,
  shouldStopHotkey,
} from "./search-keyboard";

export function openVerificationSearch(): void {
  window.dispatchEvent(new CustomEvent(OPEN_VERIFICATION_SEARCH_EVENT));
}

export function SearchButton(): ReactNode {
  return (
    <button
      type="button"
      className="vp-search-btn"
      data-verification-search-button=""
      data-action-id="shell.search-open"
      aria-label="Search"
      title="Search (⌘K or Ctrl+K)"
      onClick={() => openVerificationSearch()}
    >
      <span className="vp-nav-icon" aria-hidden="true">
        <Icon name="search" size={15} />
      </span>
      <span className="vp-search-btn-label">Search</span>
      <kbd className="vp-search-btn-kbd">⌘K</kbd>
    </button>
  );
}

type SearchLocation = {
  pathname: string;
  search: string;
  hash?: string;
};

type ResolveTaskUid = (taskId: number) => Promise<string | null>;

function legacyTaskId(hit: SearchHit): number | null {
  const idMatch = /^task:(\d+)$/.exec(hit.id);
  const id = idMatch?.[1] ?? new URL(hit.href, "http://excellent.local").searchParams.get("task");
  if (!id || !/^\d+$/.test(id)) return null;
  const parsed = Number(id);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

/** Keep the current Page mounted and express an object selection as Inspector URL state. */
export function searchInspectorHref(location: SearchLocation, uid: string): string {
  const params = new URLSearchParams(location.search);
  params.delete("task");
  params.set("peek", uid);
  params.set("pane", "inspector");
  const query = params.toString();
  return `${location.pathname}${query ? `?${query}` : ""}${location.hash ?? ""}`;
}

/**
 * Resolve a palette row without changing ordinary command or page navigation.
 * Canonical object hits already carry a UID. Legacy task hits are resolved once
 * through the typed read contract so their integer id never becomes Page state.
 */
export async function searchRowActivationHref(
  row: SearchRow,
  location: SearchLocation,
  resolveTaskUid: ResolveTaskUid,
): Promise<string> {
  if (row.kind === "command") return row.command.href;

  const objectUid = objectUidFromHref(row.hit.href);
  if (objectUid) return searchInspectorHref(location, objectUid);

  const taskId = legacyTaskId(row.hit as SearchHit);
  if (taskId == null) return row.hit.href;
  const taskUid = await resolveTaskUid(taskId);
  if (!taskUid) throw new Error("Task has no canonical object UID");
  return searchInspectorHref(location, taskUid);
}

export function SearchPalette({
  onNavigate,
}: {
  onNavigate: (to: string) => void;
}): ReactNode {
  const env = useVerificationEnvOptional();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [sel, setSel] = useState(0);
  const [activatingKey, setActivatingKey] = useState<string | null>(null);
  const [activationError, setActivationError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const activationGeneration = useRef(0);

  function restoreOpenerFocus() {
    window.requestAnimationFrame(() => {
      const opener = openerRef.current;
      if (!opener?.isConnected || opener.getClientRects().length === 0) return;
      const style = window.getComputedStyle(opener);
      if (style.display === "none" || style.visibility === "hidden") return;
      opener.focus();
    });
  }

  function close(restoreFocus = true) {
    activationGeneration.current += 1;
    setActivatingKey(null);
    setActivationError(null);
    setOpen(false);
    if (restoreFocus) restoreOpenerFocus();
  }

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(query), 150);
    return () => window.clearTimeout(id);
  }, [query]);

  useEffect(() => {
    function onOpen() {
      activationGeneration.current += 1;
      const active = document.activeElement;
      openerRef.current = active instanceof HTMLElement ? active : null;
      setActivationError(null);
      setActivatingKey(null);
      setOpen(true);
    }
    function onClose() {
      close();
    }
    function onKey(event: globalThis.KeyboardEvent) {
      const decision = decideSearchHotkey(event, { open });
      if (!shouldStopHotkey(decision)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (decision === "open") {
        onOpen();
      } else {
        close();
      }
    }
    window.addEventListener(OPEN_VERIFICATION_SEARCH_EVENT, onOpen);
    window.addEventListener(CLOSE_VERIFICATION_SEARCH_EVENT, onClose);
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener(OPEN_VERIFICATION_SEARCH_EVENT, onOpen);
      window.removeEventListener(CLOSE_VERIFICATION_SEARCH_EVENT, onClose);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setDebounced("");
      setSel(0);
      setActivatingKey(null);
      setActivationError(null);
      return;
    }
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  const q = debounced.trim();
  const args = open && q.length > 0
    ? { query: shellSearchQuery({ q, environment: env?.environment ?? null }) }
    : null;
  const { data } = useApi(search.shellGet, args, { keepPreviousData: true });
  const hits: SearchHit[] = data?.hits ?? [];
  const rows = useMemo(() => buildSearchRows(query, hits), [query, hits]);

  useEffect(() => {
    setSel(0);
  }, [query, hits.length, open]);

  async function activate(row: SearchRow | undefined) {
    if (!row || activatingKey) return;
    const generation = activationGeneration.current + 1;
    activationGeneration.current = generation;
    setActivationError(null);
    setActivatingKey(row.key);
    try {
      const href = await searchRowActivationHref(
        row,
        window.location,
        async (taskId) => {
          const task = await apiCall(tasks.get, { params: { id: taskId } });
          return task.uid?.trim() || null;
        },
      );
      if (activationGeneration.current !== generation) return;
      if (row.kind === "hit" && href !== row.hit.href) {
        const uid = new URL(href, window.location.origin).searchParams.get("peek");
        window.dispatchEvent(new CustomEvent(OBJECT_PEEK_OPEN_EVENT, { detail: { uid } }));
      }
      close(false);
      onNavigate(href);
    } catch {
      if (activationGeneration.current !== generation) return;
      setActivatingKey(null);
      setActivationError("Could not open this result. Try again.");
    }
  }

  function onInputKey(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const n = rows.length;
      if (n === 0) return;
      setSel((s) => (event.key === "ArrowDown" ? (s + 1) % n : (s - 1 + n) % n));
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      void activate(rows[sel]);
    }
  }

  if (!open) return null;

  let lastGroup: string | null = null;

  return (
    <div
      className="vp-search-palette"
      data-verification-search-palette=""
      role="dialog"
      aria-modal="true"
      aria-label="Search"
    >
      <button
        type="button"
        className="vp-search-palette-scrim"
        aria-label="Close search"
        data-action-id="shell.search-close"
        onClick={() => close()}
      />
      <div className="vp-search-palette-panel">
        <div className="vp-search">
          <span className="vp-search-icon" aria-hidden="true">
            <Icon name="search" size={14} />
          </span>
          <input
            ref={inputRef}
            type="search"
            value={query}
            placeholder="Search workflows, findings, settings…"
            aria-label="Search verification"
            data-verification-search-input=""
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKey}
          />
        </div>
        <div className="vp-search-palette-list" role="listbox" aria-label="Search results">
          {rows.length === 0 ? (
            <div className="vp-search-palette-empty">No matches.</div>
          ) : (
            rows.map((row, i) => {
              const group = row.kind === "command" ? "Commands" : row.group;
              const showGroup = group !== lastGroup;
              lastGroup = group;
              const title = row.kind === "command" ? row.command.label : row.hit.label;
              const hint =
                row.kind === "command"
                  ? row.command.retained
                    ? "Retained"
                    : row.command.shortcut
                  : row.hit.hint;
              const icon = row.kind === "command" ? row.command.icon : "search";
              return (
                <div key={row.key}>
                  {showGroup ? <div className="vp-search-palette-group">{group}</div> : null}
                  <button
                    type="button"
                    role="option"
                    aria-selected={i === sel}
                    className="vp-search-palette-row"
                    data-action-id="shell.search-result"
                    data-active={i === sel ? "true" : undefined}
                    data-search-row={row.kind === "command" ? row.command.id : row.hit.id}
                    onMouseEnter={() => setSel(i)}
                    disabled={activatingKey != null}
                    aria-busy={activatingKey === row.key ? "true" : undefined}
                    onClick={() => void activate(row)}
                  >
                    <span className="vp-nav-icon" aria-hidden="true">
                      <Icon name={icon} size={14} />
                    </span>
                    <span className="vp-search-palette-row-text">
                      <span className="vp-search-palette-row-title">{title}</span>
                      {hint ? <span className="vp-search-palette-row-hint">{hint}</span> : null}
                    </span>
                  </button>
                </div>
              );
            })
          )}
        </div>
        {activationError ? (
          <p className="vp-search-palette-empty" role="status">
            {activationError}
          </p>
        ) : null}
      </div>
    </div>
  );
}
